/**
 * Bluff bot.
 *
 * - Playing: if it holds cards of the required rank it plays them honestly (all of them, up to four). Otherwise it
 *   bluffs, putting down one or two cards — those whose rank it will need soonest last, so the ones it can unload
 *   most cheaply go first.
 * - Calling: it works out how likely the claim is to be true from the cards it holds (a claim of more cards than can
 *   still exist is a certain bluff) and calls when a bluff is more likely than not; it calls much more readily when
 *   the claim would put the claimer out. Weaker bots are less careful.
 * The bot only ever reads its own seat's view.
 */
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { bluffRank, BLUFF_MAX_PLAY, type BluffAction, type BluffLegal, type BluffView } from './engine.js';

const LOG_FACT: number[] = [0];
for (let i = 1; i <= 60; i++) LOG_FACT[i] = LOG_FACT[i - 1]! + Math.log(i);
const logChoose = (n: number, k: number) => (k < 0 || k > n ? -Infinity : LOG_FACT[n]! - LOG_FACT[k]! - LOG_FACT[n - k]!);

/** P(X = k) for X hypergeometric: `sample` cards drawn from `pop`, of which `copies` are the wanted rank. */
export function bluffPmf(pop: number, copies: number, sample: number, k: number): number {
  if (k < 0 || k > copies || k > sample || sample - k > pop - copies) return 0;
  return Math.exp(logChoose(copies, k) + logChoose(pop - copies, sample - k) - logChoose(pop, sample));
}

/** How a player without the required rank tends to bluff: mostly one card, sometimes more. */
const BLUFF_SIZE = [0, 0.7, 0.2, 0.07, 0.03];

/**
 * How likely the last claim is to be a bluff, from this seat's point of view. A player holding the rank plays every
 * card of it, so an honest claim of n means they held exactly n; a bluff means they held none. Each is weighted by how
 * likely that holding is and, for a bluff, how often a bluffer puts down n cards.
 */
export function bluffChance(view: BluffView): number {
  const claim = view.lastClaim;
  if (!claim) return 0;
  const mine = view.hand.filter((c) => bluffRank(c) === claim.rank).length;
  const copies = 4 - mine;
  if (claim.count > copies) return 1;
  const pool = 52 - view.hand.length;
  const before = view.handCounts[claim.seat]! + claim.count;
  const honest = bluffPmf(pool, copies, before, claim.count);
  const bluff = bluffPmf(pool, copies, before, 0) * (BLUFF_SIZE[claim.count] ?? 0.01);
  return honest + bluff === 0 ? 0.5 : bluff / (honest + bluff);
}

function shouldCall(view: BluffView, ctx: BotContext): boolean {
  const claim = view.lastClaim!;
  const p = bluffChance(view);
  if (p >= 1) return true;
  const claimerOut = view.handCounts[claim.seat] === 0;
  // More aggressive players call on weaker suspicion; everyone calls readily when the claim would put the claimer out.
  const threshold = claimerOut ? 0.4 : 0.62 - 0.25 * ctx.profile.aggression;
  return p >= threshold;
}

function chooseCards(view: BluffView, ctx: BotContext): string[] {
  const honest = view.hand.filter((c) => bluffRank(c) === view.required);
  if (honest.length > 0) return honest.slice(0, BLUFF_MAX_PLAY);
  // Bluff: dump the cards that will take longest to come round (the highest cyclic distance first).
  const distance = (c: string) => (((bluffRank(c) - view.required) % 13) + 13) % 13;
  const sorted = [...view.hand].sort((a, b) => distance(b) - distance(a));
  const count = view.hand.length > 4 && chance(ctx, 0.08) ? 2 : 1;
  return sorted.slice(0, Math.min(count, view.hand.length));
}

export const bluffBot: BotEngine<BluffView, BluffLegal, BluffAction> = {
  decideAction(view, legal, ctx) {
    return this.chooseCard!(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.canCall && view.lastClaim) {
      let call = shouldCall(view, ctx);
      // Weaker bots misjudge now and then.
      if (chance(ctx, ctx.profile.mistakeRate * 0.6)) call = !call;
      if (call) return { type: 'call' };
      if (!legal.canPlay) return { type: 'accept' };
    }
    if (!legal.canPlay) return legal.canAccept ? { type: 'accept' } : { type: 'call' };
    if (chance(ctx, ctx.profile.mistakeRate * 0.5)) {
      const k = 1 + ctx.rng.int(Math.min(3, view.hand.length));
      return { type: 'play', cards: view.hand.slice(0, k) };
    }
    return { type: 'play', cards: chooseCards(view, ctx) };
  },

  evaluateState(view) {
    if (view.seat === null) return 0;
    const mine = view.handCounts[view.seat]!;
    const others = view.handCounts.filter((_, s) => s !== view.seat);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (avg - mine) / 26));
  },

  calculateRisk(view) {
    return view.seat === null ? 0 : Math.min(1, bluffChance(view));
  },

  handleTimeout(view, legal) {
    if (!legal.canPlay) return legal.canAccept ? { type: 'accept' } : { type: 'call' };
    const honest = view.hand.filter((c) => bluffRank(c) === view.required);
    return { type: 'play', cards: honest.length > 0 ? honest.slice(0, BLUFF_MAX_PLAY) : [view.hand[0]!] };
  },
};
